import React, { FunctionComponent, useEffect, useState, MouseEvent } from 'react';
import {
    Grid,
    makeStyles,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableFooter,
    FormHelperText,
    Theme,
    Paper,
    Button,
    Box,
    IconButton,
    Zoom,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import gray from '@material-ui/core/colors/grey';
import { v1 as uuid } from 'uuid';
import classNames from 'classnames';

import { Delete } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import { Fee, FeeType, FeeTypeList, InvoiceType } from '../../../../store/modules/accounting/types';
import InputTextComponent from '../../../../components/InputTextField';
import { displayCurrencyAmount, getPersonSimpleName, stripNulls } from '../../../../utils/utils';
import { Contact, Person } from '../../../../store/modules/person/types';
import { Address, DropDownField } from '../../../../store/modules/application/types';
import { Api } from '../../../../store/services/services';
import { useDispatch } from 'react-redux';
import { useAuthenticationContext } from '../../../../authentication';
import { siteActions } from '../../../../store/actions';
import grey from '@material-ui/core/colors/grey';
import { Citation } from '../../../../store/modules/citation/types';
import AutoCompleteSelect from '../../../../components/AutoCompleteSelect';
import { pick } from 'lodash';

type Recipient = {
    label: string;
    value: string;
    billTo: {
        name: string;
        company?: string;
        address?: Address;
        email?: string;
    };
};

type FeesProps = {
    citation: Citation;
    getContacts: Function;
    contacts: Contact[];
    contactsLoading: boolean;
    getInvoiceTypes: Function;
    invoiceTypes: InvoiceType[];
    invoiceTypesLoading?: boolean;
    feeTypesLoading: boolean;
    getFeeTypes: Function;
    feeTypes: FeeTypeList;
    person: Person;
    getPersonDetail: Function;
    personLoading: boolean;
    updateCitation: Function;
};

type FeeViewModel = {
    key: string;
    license: string;
    type: string;
    typeError?: boolean;
    description: string;
    amount: string;
    amountError?: boolean;
    loading?: boolean;
};

function createFee(): FeeViewModel {
    return {
        key: uuid(),
        license: '',
        type: '',
        description: '',
        amount: '',
    };
}

const useStyles = makeStyles((theme: Theme) => ({
    tableRow: {
        verticalAlign: 'top',
        '&:hover ': {
            backgroundColor: '#eeeeee',
            transition: 'ease-in 200ms',
            '& button': {
                opacity: 1,
                transition: 'ease-in 200ms',
            },
        },
    },
    tableFooterCell: {
        borderBottomWidth: 0,
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        color: theme.palette.text.secondary,
        textAlign: 'right',
        marginRight: theme.spacing(2),
    },
    textField: {
        fontSize: 14,
        fontWeight: 450,
        color: gray[600],
        wordWrap: 'break-word',
        width: '100%',
    },
    textFieldInput: {
        fontSize: 13,
        border: '1px solid rgb(0,0,0,0.23) !important',
        padding: 10,
        borderRadius: 3,
        marginBottom: '5px',
        color: '#484848 !important',
    },
    editIcon: {
        opacity: 0,
    },
    amountInput: {
        textAlign: 'right',
    },
    fullWidth: {
        width: '100%',
    },
    required: {
        '&:after': {
            content: '" *"',
            color: 'red',
        },
    },
    save: {
        flex: '0 0 auto',
        display: 'flex',
        padding: 8,
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    recipientInfo: {
        marginTop: 20,
    },
    heading: {
        textTransform: 'uppercase',
        fontSize: 12,
        marginBottom: 5,
        textAlign: 'left',
        fontWeight: 500,
        color: grey[600],
        letterSpacing: 0.83,
    },
    selectRecipient: { marginBottom: 30, marginTop: 35 },
    feePanel: {
        padding: 30,
    },
    recipientInputRoot: {
        width: 320,
    },
    invoiceTypeInputRoot: {
        width: 320,
        display: 'inline-flex',
    },
    billAdress: {
        marginTop: 20,
        marginLeft: 30,
    },
}));

const RECIPIENT_PERSON = '__person';
const CITATION_RECIPIENT = '__citationRecipient';

const Fees: FunctionComponent<FeesProps> = (props) => {
    const {
        citation,
        getContacts,
        contacts,
        contactsLoading,
        getInvoiceTypes,
        invoiceTypes,
        invoiceTypesLoading,
        getFeeTypes,
        feeTypes,
        person,
        getPersonDetail,
        personLoading,
        updateCitation,
        feeTypesLoading,
    } = props;

    const dispatch = useDispatch();
    const authContext = useAuthenticationContext();

    const [invoiceType, setInvoiceType] = useState<DropDownField | null>(null);
    const [fees, setFees] = useState<FeeViewModel[]>([createFee(), createFee()]);
    const updatedFees = [...fees];

    const classes = useStyles();
    useEffect(() => {
        getInvoiceTypes();
        getFeeTypes();
    }, [getInvoiceTypes, getFeeTypes]);

    useEffect(() => {
        getContacts(citation.person.id);
        getPersonDetail(citation.person.id);
    }, [getPersonDetail, getContacts, citation]);

    useEffect(() => {
        if (citation.monetaryPenalty) {
            let existingFees = citation.monetaryPenalty.fees.map((fee) => {
                return {
                    key: uuid(),
                    license: fee.license ? fee.license.id : '',
                    type: fee.feeType ? fee.feeType.id : '',
                    description: fee.description ?? '',
                    amount: fee.amount.toString(),
                };
            });
            setFees([...existingFees, createFee()]);
        }
    }, [citation]);

    const [recipient, setRecipient] = useState<Recipient>();
    const [recipientList, setRecipientList] = useState<Recipient[]>([]);

    useEffect(() => {
        if (citation.monetaryPenalty?.invoiceType) {
            setInvoiceType({
                label: citation.monetaryPenalty?.invoiceType.name,
                value: citation.monetaryPenalty?.invoiceType.id,
            });
        } else if (!invoiceTypesLoading) {
            invoiceTypes[0] && setInvoiceType({ label: invoiceTypes[0].name, value: invoiceTypes[0].id });
        }
    }, [invoiceTypes, invoiceTypesLoading, citation.monetaryPenalty]);

    useEffect(() => {
        if (!contactsLoading && !personLoading) {
            const contactRecipients = contacts.map((contact) => {
                return {
                    label: `${getPersonSimpleName(contact.name)}`,
                    value: contact.id,
                    billTo: {
                        name: getPersonSimpleName(contact.name),
                        company: contact.company,
                        address: contact.address
                            ? {
                                  line1: contact.address.line1,
                                  line2: contact.address.line2,
                                  city: contact.address.city,
                                  county: contact.address.county,
                                  state: contact.address.state,
                                  zipcode: contact.address.zipcode,
                              }
                            : undefined,
                        email: contact.email,
                    },
                };
            });

            contactRecipients.sort((recipient1, recipient2) => {
                return recipient1.label.localeCompare(recipient2.label);
            });

            const personRecipient = {
                label: getPersonSimpleName(person.name),
                value: RECIPIENT_PERSON,
                billTo: {
                    name: getPersonSimpleName(person.name),
                    address: person.address
                        ? {
                              line1: person.address.line1,
                              line2: person.address.line2,
                              city: person.address.city,
                              county: person.address.county,
                              state: person.address.state,
                              zipcode: person.address.zipcode,
                          }
                        : undefined,
                    email: person.email,
                },
            };

            let recipientValue;

            const contactValue = contacts.find((contact) => contact.name === citation.monetaryPenalty?.billTo.name);

            if (person.name === citation.monetaryPenalty?.billTo.name) {
                recipientValue = RECIPIENT_PERSON;
            } else if (contactValue) {
                recipientValue = contactValue.id;
            } else {
                recipientValue = CITATION_RECIPIENT;
            }

            if (citation.monetaryPenalty?.billTo) {
                let citationRecipient = {
                    label: citation.monetaryPenalty?.billTo.name,
                    value: recipientValue,
                    billTo: {
                        name: citation.monetaryPenalty?.billTo.name,
                        address: citation.monetaryPenalty?.billTo.address
                            ? {
                                  line1: citation.monetaryPenalty?.billTo.address?.line1,
                                  line2: citation.monetaryPenalty?.billTo.address?.line2,
                                  city: citation.monetaryPenalty?.billTo.address?.city,
                                  county: citation.monetaryPenalty?.billTo.address?.county,
                                  zipcode: citation.monetaryPenalty?.billTo.address?.zipcode,
                                  state: citation.monetaryPenalty?.billTo.address?.state,
                              }
                            : undefined,
                        email: citation.monetaryPenalty?.billTo.email,
                    },
                };
                setRecipient(citationRecipient);
            } else {
                setRecipient(personRecipient);
            }

            setRecipientList([personRecipient, ...contactRecipients]);
        }
    }, [contacts, contactsLoading, person, personLoading, citation.monetaryPenalty]);

    function validateFee(fee: FeeViewModel, validatePartial: boolean) {
        if ((fee.type && fee.amount) || (!fee.type && !fee.amount)) {
            fee.typeError = false;
            fee.amountError = false;
        } else if (validatePartial) {
            if (fee.type && !fee.amount) {
                fee.amountError = true;
            } else if (!fee.type && fee.amount) {
                fee.typeError = true;
            }
        }
    }

    const handleDeleteRow = (id: string, event: MouseEvent) => {
        event.stopPropagation();
        const filteredEntries = fees.filter((item) => item.key !== id);
        setFees(filteredEntries);
    };

    function isFeeDataValid(fees: FeeViewModel[]): boolean {
        // Data is valid if there is at least one entry with data and none of the other entries have errors.
        return (
            !fees.some((fee) => fee.typeError || fee.amountError) &&
            fees.some((fee) => fee.license && fee.type && fee.amount)
        );
    }

    const handleSave = () => {
        if (!invoiceType) {
            return;
        }
        // Re-run validation since the user may have been in the middle of entering an entry.
        const validatedFees = [...fees];
        validatedFees.forEach((fee) => validateFee(fee, true));

        if (!isFeeDataValid(validatedFees)) {
            setFees(validatedFees);
            return;
        }
        const selectedInvoiceType: InvoiceType | undefined = invoiceTypes.find((type) => {
            return type.id === invoiceType!.value;
        });

        const updatedCitation = {
            ...citation,
            monetaryPenalty: {
                invoiceType: {
                    id: selectedInvoiceType?.id,
                    name: selectedInvoiceType?.name,
                },
                billTo: recipient?.billTo,
                fees: fees
                    .filter((fee) => {
                        return fee.license !== '';
                    })
                    .map((fee) => {
                        return {
                            description: fee.description,
                            amount: parseInt(fee.amount),
                            feeType: pick(
                                feeTypes.find((feeType) => {
                                    return feeType.id === fee.type;
                                }),
                                ['id', 'name'],
                            ),
                            license: citation.licenses
                                ? pick(
                                      citation.licenses.find((license) => {
                                          return license.id === fee.license;
                                      }),
                                      ['id', 'type', 'licenseNumber'],
                                  )
                                : null,
                        };
                    }),
            },
        };

        stripNulls(updatedCitation);

        updateCitation(updatedCitation, citation.id, true);
    };

    const changeInvoiceType = (data: DropDownField | null) => {
        setInvoiceType(data);
    };

    const changeRecipient = (data: DropDownField | null) => {
        const recipient = recipientList.find((recipient: Recipient) => recipient.value === data?.value);
        setRecipient(recipient);
    };

    const generateFee = async (feeType: FeeType, viewModel: FeeViewModel): Promise<Fee | undefined> => {
        try {
            viewModel.loading = true;

            const token = await authContext.getAccessToken({ scopes: [authContext.scopes['feeTypes.read']] });
            const data =
                feeType.entityType === 'License'
                    ? citation.licenses?.find((license) => license.id === viewModel.license)
                    : feeType.entityType === 'Person'
                    ? person
                    : undefined;

            return Api.generateFee(token.accessToken, feeType.id, data);
        } catch (err) {
            dispatch(siteActions.setError(err));

            return undefined;
        } finally {
            viewModel.loading = false;
        }
    };

    const updateFee = async (
        key: string,
        property: keyof Omit<FeeViewModel, 'key' | 'typeError' | 'amountError' | 'loading'>,
        value: string | null,
    ) => {
        const fee = updatedFees.find((fee) => fee.key === key);

        if (fee) {
            // Check if removing value from a property that currently has a value.
            const valueRemoved: boolean = !!fee[property] && !value;
            if (value !== null) {
                fee[property] = value;
            } else {
                fee[property] = '';
            }

            // Only validate partial entries if a value was removed.  This avoids showing
            // errors while the user is entering data, i.e. showing errors before the user
            // has entered all the fields.
            validateFee(fee, valueRemoved);

            switch (property) {
                case 'type':
                    // Populate fee details.

                    const feeType = feeTypes.find((feeType) => feeType.id === fee.type);

                    if (feeType) {
                        const feeDetails = await generateFee(feeType, fee);

                        fee.description = feeDetails?.description || '';
                        fee.amount = (feeDetails?.amount && feeDetails?.amount.toFixed(2)) || '';
                    }

                    break;
            }

            if (updatedFees[updatedFees.length - 1].type) {
                // Add new blank entry if everything is filled out.
                updatedFees.push(createFee());
            }

            setFees(updatedFees);
        }
    };

    const isValid = invoiceType && isFeeDataValid(fees);
    const total = fees.reduce((accum, fee) => accum + (fee.amount ? parseFloat(fee.amount) : 0), 0);

    return (
        <>
            <Paper className={classes.feePanel}>
                <Grid container>
                    <Grid xs={5} container alignItems="center">
                        {!invoiceTypesLoading ? (
                            invoiceTypes?.length ? (
                                <>
                                    <Grid item container alignItems="center">
                                        <Grid item xs={3} alignItems="flex-start" container direction="column">
                                            <Typography className={classes.label}>Invoice Type</Typography>
                                        </Grid>
                                        <Grid item xs={9}>
                                            <AutoCompleteSelect
                                                data={invoiceTypes.map((type) => ({
                                                    label: type.name,
                                                    value: type.id,
                                                }))}
                                                isMultipleValue={false}
                                                onChange={changeInvoiceType}
                                                inputSize={'small'}
                                                disableClearable={true}
                                                value={invoiceType}
                                                classes={{
                                                    inputRoot: classes.invoiceTypeInputRoot,
                                                }}
                                                variant={'outlined'}
                                                ariaLabel="Invoice Type"
                                            />
                                        </Grid>
                                    </Grid>
                                </>
                            ) : (
                                <></>
                            )
                        ) : (
                            <Skeleton variant="text" width="30%" />
                        )}
                        {!invoiceTypesLoading && !invoiceTypes?.length && (
                            <Typography color="error">No invoice types configured</Typography>
                        )}
                        {recipient && !personLoading ? (
                            <Grid container item className={classes.selectRecipient}>
                                <Grid container item direction="column" alignItems="flex-start" xs={12}>
                                    <Typography className={classes.heading}>Choose Recipient</Typography>
                                </Grid>

                                <Grid container item alignItems="center" className={classes.recipientInfo}>
                                    <Grid item xs={3}>
                                        <Typography className={classes.label}>Recipient</Typography>
                                    </Grid>
                                    <Grid item xs={9} container direction="column" alignItems="flex-start">
                                        <AutoCompleteSelect
                                            data={recipientList.map((recipient) => ({
                                                label: recipient.label,
                                                value: recipient.value,
                                            }))}
                                            onChange={changeRecipient}
                                            inputSize={'small'}
                                            value={
                                                recipient.value
                                                    ? {
                                                          label: recipient.label,
                                                          value: recipient.value,
                                                      }
                                                    : null
                                            }
                                            classes={{
                                                inputRoot: classes.recipientInputRoot,
                                            }}
                                            disableClearable={true}
                                            variant={'outlined'}
                                            ariaLabel="Recipient"
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container item className={classes.recipientInfo}>
                                    <Grid item xs={3}>
                                        <Typography className={classes.label}>Send To</Typography>
                                    </Grid>
                                    <Grid item xs={9}>
                                        <Typography>{recipient.billTo.name}</Typography>
                                        {recipient.billTo.company && (
                                            <Typography>{recipient.billTo.company}</Typography>
                                        )}
                                        <Typography>{recipient.billTo.address?.line1}</Typography>
                                        <Typography>{recipient.billTo.address?.line2}</Typography>
                                        <Typography>
                                            {recipient.billTo?.address?.city} {recipient.billTo.address?.state}{' '}
                                            {recipient.billTo.address?.zipcode}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : (
                            <Skeleton variant="text" width="30%" />
                        )}
                    </Grid>
                </Grid>

                <TableContainer>
                    <Typography className={classes.heading}>Select Licenses</Typography>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell width="25%">License Number</TableCell>
                                <TableCell width="25%">
                                    <span className={classes.required}>Item</span>
                                </TableCell>
                                <TableCell width="35%">Description</TableCell>

                                <TableCell width="14%">
                                    <span className={classes.required}>Amount</span>
                                </TableCell>
                                <TableCell width="1%"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fees.map((fee, index) => (
                                <TableRow key={fee.key} className={classes.tableRow}>
                                    <TableCell>
                                        {citation.licenses && (
                                            <AutoCompleteSelect
                                                data={citation.licenses.map((license) => ({
                                                    label: license.licenseNumber || '',
                                                    value: license.id || '',
                                                }))}
                                                isMultipleValue={false}
                                                onChange={(data: DropDownField | null) => {
                                                    const value = data !== null ? data.value : null;
                                                    updateFee(fee.key, 'license', value);
                                                }}
                                                inputSize={'small'}
                                                variant={'outlined'}
                                                ariaLabel="Licenses"
                                                disableClearable={true}
                                                value={
                                                    citation.licenses
                                                        .map((license) => {
                                                            return {
                                                                label: license.licenseNumber,
                                                                value: license.id,
                                                            };
                                                        })
                                                        .find((license) => {
                                                            return license.value === fee.license;
                                                        }) || null
                                                }
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {!feeTypesLoading ? (
                                            feeTypes?.length ? (
                                                <>
                                                    <AutoCompleteSelect
                                                        data={
                                                            Array.isArray(feeTypes)
                                                                ? feeTypes?.map((feeType) => ({
                                                                      label: feeType.name,
                                                                      value: feeType.id,
                                                                  }))
                                                                : []
                                                        }
                                                        isMultipleValue={false}
                                                        onChange={(data: DropDownField | null) => {
                                                            const value = data !== null ? data.value : null;

                                                            updateFee(fee.key, 'type', value);
                                                        }}
                                                        disableClearable={true}
                                                        inputSize={'small'}
                                                        value={
                                                            feeTypes
                                                                .map((feeType: any) => {
                                                                    return {
                                                                        label: feeType?.name,
                                                                        value: feeType?.id,
                                                                    };
                                                                })
                                                                .find((feeType: any) => {
                                                                    return feeType.value === fee.type;
                                                                }) || null
                                                        }
                                                        ariaLabel="Type"
                                                        variant={'outlined'}
                                                    />
                                                    {fee.typeError && (
                                                        <FormHelperText error>Type is required</FormHelperText>
                                                    )}
                                                </>
                                            ) : (
                                                <></>
                                            )
                                        ) : (
                                            <Skeleton variant="text" width="30%" />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <InputTextComponent
                                            id={`description-${fee.key}`}
                                            disabled={fee.loading}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classes.textFieldInput}
                                            onChange={(description: string) => {
                                                updateFee(fee.key, 'description', description);
                                            }}
                                            value={fee.description}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <InputTextComponent
                                            id={`amount-${fee.key}`}
                                            disabled={fee.loading}
                                            size="small"
                                            fontSize="13"
                                            className={classes.textField}
                                            inputProps={classNames(classes.textFieldInput, classes.amountInput)}
                                            onChange={(amount: string) => updateFee(fee.key, 'amount', amount)}
                                            value={fee.amount}
                                            inputType={'currency'}
                                            error={fee.amountError}
                                        />
                                        {fee.amountError && <FormHelperText error>Amount is required</FormHelperText>}
                                    </TableCell>

                                    <TableCell align="right">
                                        <Box
                                            style={{
                                                display: 'flex',
                                                alignContent: 'center',
                                                alignItems: 'center',
                                            }}
                                        >
                                            {index !== fees.length - 1 ? (
                                                <Tooltip
                                                    disableFocusListener
                                                    disableTouchListener
                                                    TransitionComponent={Zoom}
                                                    title="Delete"
                                                >
                                                    <IconButton
                                                        onClick={(e: MouseEvent<HTMLElement>) =>
                                                            handleDeleteRow(fee.key, e)
                                                        }
                                                        className={classes.editIcon}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TableCell align="right" colSpan={6} className={classes.tableFooterCell}>
                                    <Typography component="span" className={classes.label}>
                                        Total:
                                    </Typography>
                                    <Typography component="span">{displayCurrencyAmount(total)}</Typography>
                                </TableCell>
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
                <Box className={classes.save}>
                    <Button
                        variant="contained"
                        disableElevation
                        color="primary"
                        disabled={!isValid}
                        onClick={handleSave}
                    >
                        Save
                    </Button>
                </Box>
            </Paper>
        </>
    );
};

export default Fees;
